import { useState, useCallback } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  DescriptionItem,
  DescriptionList,
  Tooltip,
} from '@billda/design-system';
import {
  VisibilityIcon,
  VisibilityOffIcon,
  CopyIcon,
  CheckCircleIcon,
} from '@billda/design-system';
import type { UserPaymentDetailsDto } from '../../../api/userManagementApi';

interface UserPaymentDetailsCardProps {
  paymentDetails: UserPaymentDetailsDto;
}

// Masking utilities following mobile pattern
const maskAccountNumber = (accountNumber: string | null): string => {
  if (!accountNumber || accountNumber.length < 4) {
    return '-';
  }
  return `••••${accountNumber.slice(-4)}`;
};

const maskSortCode = (sortCode: string | null): string => {
  if (!sortCode || sortCode.length < 2) {
    return '-';
  }
  return `••-••-${sortCode.slice(-2)}`;
};

const formatSortCode = (sortCode: string | null): string => {
  if (!sortCode || sortCode.length !== 6) {
    return sortCode || '-';
  }
  return `${sortCode.slice(0, 2)}-${sortCode.slice(2, 4)}-${sortCode.slice(4, 6)}`;
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) {
    return '-';
  }
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

function SectionTitle({
  title,
  className,
}: {
  title: string;
  className?: string;
}) {
  return (
    <h4
      className={`type-body-md font-semibold text-content-primary ${className ?? ''}`}
    >
      {title}
    </h4>
  );
}

export function UserPaymentDetailsCard({
  paymentDetails,
}: UserPaymentDetailsCardProps) {
  const [isRevealed, setIsRevealed] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = useCallback(
    async (value: string | null, fieldName: string) => {
      if (!value) {
        return;
      }
      try {
        await navigator.clipboard.writeText(value);
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(null), 3000);
      } catch {
        // Silently fail
      }
    },
    []
  );

  const { bankDetails, address, cscsCard, dateOfBirth, isComplete } =
    paymentDetails;

  const hasBankDetails = bankDetails?.accountNumber || bankDetails?.sortCode;
  const hasAddress = address?.line1;
  const hasCscsCard = cscsCard?.cardNumber;

  return (
    <Card data-testid="user-payment-details-card">
      <CardHeader>
        <h3 className="type-h6">Payment Details</h3>
        {hasBankDetails && (
          <Tooltip
            content={isRevealed ? 'Hide bank details' : 'Reveal bank details'}
          >
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsRevealed(!isRevealed)}
              aria-label={
                isRevealed ? 'Hide bank details' : 'Reveal bank details'
              }
              data-testid="toggle-reveal-button"
            >
              {isRevealed ? (
                <VisibilityOffIcon aria-hidden="true" className="size-4" />
              ) : (
                <VisibilityIcon aria-hidden="true" className="size-4" />
              )}
              {isRevealed ? 'Hide' : 'Reveal'}
            </Button>
          </Tooltip>
        )}
      </CardHeader>
      <CardContent className="pt-4">
        <div className="flex max-w-2xl flex-col gap-5">
          {/* Bank Details Section */}
          {hasBankDetails && (
            <>
              <SectionTitle title="Bank Account" />
              <DescriptionList className="type-body-md">
                {bankDetails?.accountNumber && (
                  <CopyableDetailRow
                    label="Account Number"
                    value={
                      isRevealed
                        ? bankDetails.accountNumber
                        : maskAccountNumber(bankDetails.accountNumber)
                    }
                    rawValue={bankDetails.accountNumber}
                    canCopy={isRevealed}
                    onCopy={handleCopy}
                    copiedField={copiedField}
                    fieldName="accountNumber"
                    testId="account-number"
                  />
                )}
                {bankDetails?.sortCode && (
                  <CopyableDetailRow
                    label="Sort Code"
                    value={
                      isRevealed
                        ? formatSortCode(bankDetails.sortCode)
                        : maskSortCode(bankDetails.sortCode)
                    }
                    rawValue={bankDetails.sortCode}
                    canCopy={isRevealed}
                    onCopy={handleCopy}
                    copiedField={copiedField}
                    fieldName="sortCode"
                    testId="sort-code"
                  />
                )}
                {bankDetails?.nameOnAccount && (
                  <DetailRow
                    label="Name on Account"
                    value={bankDetails.nameOnAccount}
                    testId="name-on-account"
                  />
                )}
              </DescriptionList>
            </>
          )}

          {/* Address Section */}
          {hasAddress && (
            <>
              <SectionTitle title="Address" />
              <DescriptionList className="type-body-md">
                {address?.line1 && (
                  <DetailRow
                    label="Address Line 1"
                    value={address.line1}
                    testId="address-line1"
                  />
                )}
                {address?.line2 && (
                  <DetailRow
                    label="Address Line 2"
                    value={address.line2}
                    testId="address-line2"
                  />
                )}
                {address?.city && (
                  <DetailRow
                    label="City"
                    value={address.city}
                    testId="address-city"
                  />
                )}
                {address?.county && (
                  <DetailRow
                    label="County"
                    value={address.county}
                    testId="address-county"
                  />
                )}
                {address?.postcode && (
                  <DetailRow
                    label="Postcode"
                    value={address.postcode}
                    testId="address-postcode"
                  />
                )}
                {address?.country && (
                  <DetailRow
                    label="Country"
                    value={address.country}
                    testId="address-country"
                  />
                )}
              </DescriptionList>
            </>
          )}

          {/* CSCS Card Section */}
          {hasCscsCard && (
            <>
              <SectionTitle title="CSCS Card" />
              <DescriptionList className="type-body-md">
                {cscsCard?.cardNumber && (
                  <DetailRow
                    label="Card Number"
                    value={cscsCard.cardNumber}
                    testId="cscs-card-number"
                  />
                )}
                {cscsCard?.expiryDate && (
                  <DetailRow
                    label="Expiry Date"
                    value={formatDate(cscsCard.expiryDate)}
                    testId="cscs-expiry-date"
                  />
                )}
              </DescriptionList>
            </>
          )}

          {/* Personal Info */}
          {dateOfBirth && (
            <>
              <SectionTitle title="Personal Information" />
              <DescriptionList className="type-body-md">
                <DetailRow
                  label="Date of Birth"
                  value={formatDate(dateOfBirth)}
                  testId="date-of-birth"
                />
              </DescriptionList>
            </>
          )}

          {/* Completion Status */}
          <DescriptionList className="type-body-md">
            <DescriptionItem
              label="Status"
              value={
                <Chip
                  label={isComplete ? 'Complete' : 'Incomplete'}
                  size="sm"
                  color={isComplete ? 'success' : 'warning'}
                />
              }
            />
          </DescriptionList>
        </div>
      </CardContent>
    </Card>
  );
}

// Sub-components
interface DetailRowProps {
  label: string;
  value: string;
  testId: string;
}

function DetailRow({ label, value, testId }: DetailRowProps) {
  return (
    <DescriptionItem
      label={label}
      value={<span data-testid={testId}>{value}</span>}
    />
  );
}

interface CopyableDetailRowProps extends DetailRowProps {
  rawValue: string | null;
  canCopy: boolean;
  onCopy: (value: string | null, fieldName: string) => void;
  copiedField: string | null;
  fieldName: string;
}

function CopyableDetailRow({
  label,
  value,
  rawValue,
  canCopy,
  onCopy,
  copiedField,
  fieldName,
  testId,
}: CopyableDetailRowProps) {
  return (
    <DescriptionItem
      label={label}
      value={
        <div className="flex items-center gap-0.5">
          <span className="type-body-md font-medium" data-testid={testId}>
            {value}
          </span>
          {canCopy && rawValue && (
            <Tooltip content={copiedField === fieldName ? 'Copied!' : 'Copy'}>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onCopy(rawValue, fieldName)}
                aria-label={
                  copiedField === fieldName ? 'Copied' : `Copy ${label}`
                }
                className="ml-0.5 size-7 p-0.5"
                data-testid={`copy-${fieldName}-button`}
              >
                {copiedField === fieldName ? (
                  <CheckCircleIcon
                    aria-hidden="true"
                    className="size-4 text-content-success"
                  />
                ) : (
                  <CopyIcon aria-hidden="true" className="size-4" />
                )}
              </Button>
            </Tooltip>
          )}
        </div>
      }
    />
  );
}
